import deepmerge from 'deepmerge'

import { SESSION_STATUSES } from '~/core/constants'

import { getSalesforceUrl, selectMeetingPreviewEnabled } from '~/selectors/room-settings'

import { getAuthenticated, getIsElectron, getShouldLaunchUndocked } from '~/selectors/app'
import { getShowRecordingPrompt, isDeepLinkingEnabled } from './settings'

const getState = state => state.activeSession

export const getDetails = state => getState(state).details
export const getRoomName = state => getDetails(state).roomName
export const getSubject = state => getDetails(state).subject
export const getCustomRoomConfigs = state => getDetails(state).customRoomConfigs

/**
 * Builds the arguments for Jitsi's `setMeetingTimer` iframe-API command from
 * the calendar event this participant joined through, or null when there is no
 * usable event (ad-hoc meeting — no timer).
 *
 * Semantics — intentional, not a sync bug: the timer is a PER-PARTICIPANT
 * "personal agenda" timer keyed to the event THIS participant joined through
 * (`getDetails().event`), not a shared room clock. Two different calendar
 * events can reuse the same link; each group sees the timer for their own
 * booked slot, which is the correct signal when one slot runs into the next.
 * Ad-hoc joiners (no event) get no timer.
 *
 * `elapsed` is seconds since the scheduled START and is intentionally NOT
 * clamped: it may be negative (joined before the scheduled start — the timer
 * sits at the full duration until the start arrives) or exceed the duration
 * (joined after the scheduled end — lands directly in the overrun state).
 * Jitsi derives remaining / overrun / expired / lap state from it.
 *
 * @param {Object} state - The redux state.
 * @returns {{ duration: number, elapsed: number }|null}
 */
export const getMeetingTimerArgs = state => {
  const event = getDetails(state)?.event

  if (!event?.start || !event?.end || event.end <= event.start) {
    return null
  }

  return {
    duration: Math.round((event.end - event.start) / 1000),
    elapsed: Math.round((Date.now() - event.start) / 1000)
  }
}
export const getStartSilent = state => getCustomRoomConfigs(state).config.startSilent
export const getSessionStatus = state => getState(state).sessionStatus
export const getStartTime = state => getState(state).startTime
export const getWaitingToJoin = state => getState(state).waitingToJoin

export const isSessionConnected = state => {
  const sessionStatus = getSessionStatus(state)
  return sessionStatus === SESSION_STATUSES.CONNECTED
}

export const getIsSessionClosed = state => {
  const sessionStatus = getSessionStatus(state)
  return sessionStatus === SESSION_STATUSES.CLOSED
}

export const getIsMeetingLaunched = state => {
  const sessionStatus = getSessionStatus(state)

  return [SESSION_STATUSES.CONNECTING, SESSION_STATUSES.CONNECTED].includes(sessionStatus)
}

export const getDetailsForJoinCallback = state => {
  const { event, roomName, url, ...rest } = getDetails(state)

  const result = { event, roomName, url }

  if (!url && rest) {
    result.url = rest.eventRoom?.meetingLink || rest.customRoom?.url || rest.personalRoom?.url
  }

  return result
}

export const getInterfaceConfig = state => {
  const customRoomInterfaceConfig = getCustomRoomConfigs(state).interfaceConfig

  const interfaceConfig = {
    ENABLE_DIAL_OUT: true,
    DISABLE_TRANSCRIPTION_SUBTITLES: false,
    SETTINGS_SECTIONS: ['devices', 'language', 'moderator', 'profile', 'sounds', 'more']
  }

  return {
    ...interfaceConfig,
    ...customRoomInterfaceConfig
  }
}

export const getConfig = state => {
  const authenticated = getAuthenticated(state)
  const customRoomConfig = deepmerge({}, getCustomRoomConfigs(state).config)
  const salesforceUrl = getSalesforceUrl(state)
  const shouldLaunchUndocked = getShouldLaunchUndocked(state)
  const showRecordingPrompt = getShowRecordingPrompt(state)
  const isPreviewEnabled = selectMeetingPreviewEnabled(state)

  customRoomConfig.recordings.suggestRecording = showRecordingPrompt

  if (authenticated) {
    customRoomConfig.prejoinConfig.hideDisplayName = true
  }
  customRoomConfig.toolbarButtons.push('livestreaming')
  customRoomConfig.toolbarButtons.push('recording')
  customRoomConfig.toolbarButtons.push('closedcaptions')

  if (salesforceUrl) {
    customRoomConfig.toolbarButtons.push('linktosalesforce')
  }

  if (!shouldLaunchUndocked) {
    customRoomConfig.prejoinConfig.enabled = false
  }

  const config = {
    ...customRoomConfig,
    disableProfile: authenticated,
    salesforceUrl,
    pip: {
      disabled: !isPreviewEnabled,
      showOnPrejoin: true
    }
  }

  if (!isDeepLinkingEnabled(state) || !getIsElectron(state)) {
    config.deeplinking = {
      disabled: true
    }
  }

  // NOTE: the calendar-driven meeting timer is no longer fed through
  // configOverwrite — Jitsi dropped the `timeTimer.defaultDuration` /
  // `defaultElapsed` config keys in favour of the `setMeetingTimer` iframe-API
  // command. The duration/elapsed are now pushed at room-join via
  // `getMeetingTimerArgs` (see handleRoomJoined in actions/meeting-launch).

  return config
}
